import { ArrowDown, ArrowUp, Copy, Trash } from '@/components/icons';
import {
    TooltipIconButton,
    type TooltipIconButtonProps,
} from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { getCustomSurveyQuestionElementId } from '../builderUtils';
import { QuestionEditor } from './QuestionEditor';
import { QuestionPreview } from './QuestionPreview';
import type { BuilderAction } from '../builderReducer';
import type { SurveyQuestion } from '@/api/surveys';
import type { Dispatch, ReactNode } from 'react';

interface QuestionCardProps {
    canMoveDown: boolean;
    canMoveUp: boolean;
    dispatch: Dispatch<BuilderAction>;
    isActive: boolean;
    question: SurveyQuestion;
    questionNumber: number;
}

interface QuestionActionButtonProps {
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    label: string;
    onClick: () => void;
    variant?: TooltipIconButtonProps['variant'];
}

function QuestionActionButton({
    children,
    className,
    disabled,
    label,
    onClick,
    variant = 'ghost',
}: QuestionActionButtonProps) {
    return (
        <TooltipIconButton
            label={label}
            variant={variant}
            size="iconSm"
            className={cn('rounded-xl', className)}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </TooltipIconButton>
    );
}

export function QuestionCard({
    canMoveDown,
    canMoveUp,
    dispatch,
    isActive,
    question,
    questionNumber,
}: QuestionCardProps) {
    const questionId = question.id;
    if (!questionId) return null;

    const questionActions = isActive ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 rounded-2xl bg-surface/40 p-1">
            <QuestionActionButton
                label="Subir"
                disabled={!canMoveUp}
                onClick={() =>
                    dispatch({
                        type: 'moveQuestion',
                        questionId,
                        direction: 'up',
                    })
                }
            >
                <ArrowUp className="size-4" />
            </QuestionActionButton>
            <QuestionActionButton
                label="Descer"
                disabled={!canMoveDown}
                onClick={() =>
                    dispatch({
                        type: 'moveQuestion',
                        questionId,
                        direction: 'down',
                    })
                }
            >
                <ArrowDown className="size-4" />
            </QuestionActionButton>
            <QuestionActionButton
                label="Duplicar"
                onClick={() => dispatch({ type: 'duplicateQuestion', questionId })}
            >
                <Copy className="size-4" />
            </QuestionActionButton>
            <QuestionActionButton
                label="Excluir"
                variant="ghostDestructive"
                onClick={() => dispatch({ type: 'deleteQuestion', questionId })}
            >
                <Trash className="size-4" />
            </QuestionActionButton>
        </div>
    ) : null;

    return (
        <article
            id={getCustomSurveyQuestionElementId(questionId)}
            className={cn(
                'group scroll-mt-28 rounded-2xl border bg-card transition-all',
                isActive
                    ? 'border-brand/50 bg-brand-subtle/20 shadow-none'
                    : 'border-border/80 shadow-sm hover:border-brand/30 hover:shadow-elevation-1',
            )}
        >
            <div className="flex flex-col gap-3 px-4 py-3 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
                <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() =>
                        dispatch({
                            type: 'setActiveQuestion',
                            questionId: isActive ? null : questionId,
                        })
                    }
                >
                    <div className="min-w-0 space-y-1.5">
                        <p className="line-clamp-2 text-sm font-semibold text-foreground sm:text-base">
                            {questionNumber}. {question.field_label || 'Pergunta sem enunciado'}
                            {question.required ? (
                                <span className="ml-1 text-destructive">*</span>
                            ) : null}
                        </p>
                    </div>
                </button>
                {questionActions}
            </div>

            <div className="px-4 pb-4 sm:px-5">
                {isActive ? (
                    <QuestionEditor dispatch={dispatch} question={question} />
                ) : (
                    <QuestionPreview question={question} />
                )}
            </div>
        </article>
    );
}
